// Sitemap utilities for the /sitemap admin/info page.
//
// The actual sitemap search engines see is generated server-side, dynamically from
// live DB data, at /sitemap.xml (see backend/server.js). This file fetches and parses
// that single source of truth for display here, instead of maintaining a separate
// client-side generator — the previous version of this file re-implemented its own
// sitemap generation, which had already drifted out of sync with the real one (e.g.
// missing pages the backend version included).

const BASE_URL = 'https://thedeepwave.com';

// Static routes shown under the "Static Pages" section — kept here only for display
// grouping; the actual page list still comes from the real sitemap.xml.
const STATIC_ROUTES = [
    '/', '/feed', '/feed/audio', '/feed/videos', '/feed/images', '/feed/posts',
    '/discover', '/radio', '/gallery', '/community', '/calendar', '/privacy', '/terms',
    '/support', '/subscriptions', '/login', '/register'
];

// Fetch the raw XML sitemap from the server (the same file submitted to search engines)
export const fetchXMLSitemap = async () => {
    const response = await fetch('/sitemap.xml');
    if (!response.ok) {
        throw new Error(`Failed to fetch sitemap.xml (${response.status})`);
    }
    return response.text();
};

// Parse the server's XML sitemap into plain entry objects
const parseXMLSitemap = (xmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'application/xml');

    return Array.from(doc.getElementsByTagName('url')).map(node => ({
        loc: node.getElementsByTagName('loc')[0]?.textContent || '',
        lastmod: node.getElementsByTagName('lastmod')[0]?.textContent || '',
        changefreq: node.getElementsByTagName('changefreq')[0]?.textContent || 'weekly',
        priority: node.getElementsByTagName('priority')[0]?.textContent || '0.5'
    })).filter(entry => entry.loc);
};

// Group flat entries into the categories the sitemap page displays
const groupEntries = (entries) => {
    const grouped = {
        static: [], profiles: [], posts: [], audio: [], video: [], images: [], events: [], artists: [], albums: []
    };

    entries.forEach(entry => {
        const path = entry.loc.replace(BASE_URL, '');

        if (STATIC_ROUTES.includes(path)) {
            grouped.static.push(entry);
        } else if (path.startsWith('/view/post/')) {
            grouped.posts.push(entry);
        } else if (path.startsWith('/view/audio/')) {
            grouped.audio.push(entry);
        } else if (path.startsWith('/view/video/')) {
            grouped.video.push(entry);
        } else if (path.startsWith('/view/image/')) {
            grouped.images.push(entry);
        } else if (path.startsWith('/album/')) {
            grouped.albums.push(entry);
        } else if (path.startsWith('/events/')) {
            grouped.events.push(entry);
        } else if (path.includes('/artist/')) {
            grouped.artists.push(entry);
        } else if (path.startsWith('/profile/')) {
            grouped.profiles.push(entry);
        }
    });

    return grouped;
};

// Cache to avoid refetching on every render
let sitemapCache = null;
let lastGenerated = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getCachedSitemap = async (format = 'json') => {
    const now = Date.now();

    if (!sitemapCache || !lastGenerated || (now - lastGenerated) > CACHE_DURATION) {
        const xml = await fetchXMLSitemap();
        const entries = parseXMLSitemap(xml);
        const grouped = groupEntries(entries);

        sitemapCache = {
            xml,
            json: {
                metadata: {
                    generated: new Date().toISOString(),
                    total_urls: entries.length,
                    base_url: BASE_URL
                },
                ...grouped
            }
        };
        lastGenerated = now;
    }

    return sitemapCache[format];
};
